import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../hooks/queries/useUserQuery';
import { useCreditBalance } from '../hooks/queries/useCreditBalanceQuery';
import { apiClient } from '../services/api';
import LoadingOverlay from './LoadingOverlay';

interface CreditBalanceProps {
  userId: string;
}

export function CreditBalance({ userId }: CreditBalanceProps) {
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { user: userData } = useUser(user?.id);
  const { data: credits, isLoading: loading } = useCreditBalance(userId);

  // Check if user has active subscription
  const hasActiveSubscription = userData?.subscription_status === 'active' || userData?.subscription_status === 'canceling';

  // Open Stripe Customer Portal (no email login needed!)
  const openStripePortal = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!user?.id) {
      console.error('❌ [CreditBalance] No user ID available');
      alert('Unable to open billing portal. Please try refreshing the page.');
      return;
    }

    if (isLoadingPortal) {
      return; // Prevent multiple clicks
    }

    try {
      setIsLoadingPortal(true);

      // Create portal session via API
      const { url } = await apiClient.createPortalSession(
        user.id,
        `${window.location.origin}/my-collection`
      );

      // Redirect to Stripe portal (no email needed!)
      window.location.href = url;
    } catch (error) {
      console.error('❌ [CreditBalance] Failed to open billing portal:', error);

      // Close overlay first
      setIsLoadingPortal(false);

      // Show error after a brief delay to ensure overlay closes
      setTimeout(() => {
        const errorMessage = error instanceof Error ? error.message : 'Unable to open billing portal. Please try again.';
        alert(`${errorMessage}\n\nNote: If you're on dev environment, make sure you have a test subscription created with dev's test Stripe keys.`);
      }, 100);
    }
  };

  const handleClick = async (e: React.MouseEvent) => {
    if (hasActiveSubscription) {
      // User has subscription → open Stripe portal
      await openStripePortal(e);
    } else {
      // User has no subscription → navigate to upgrade page
      navigate('/upgrade');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-text-muted">
        <div className="w-4 h-4 border-2 border-text-subtle border-t-transparent rounded-full animate-spin" />
        <span className="text-body-sm">Loading...</span>
      </div>
    );
  }

  if (credits === null || credits === undefined) {
    return (
      <div className="flex items-center gap-2 text-text-muted">
        <span className="text-body-sm">Credits unavailable</span>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isLoadingPortal}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-hover-strong rounded-button border border-border cursor-pointer hover:bg-hover-strong/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg
          className="w-4 h-4 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="text-white font-medium text-body-sm">{credits}</span>
        <span className="text-text-muted text-body-sm">Credits</span>
      </button>

      {/* Loading overlay for portal session */}
      <LoadingOverlay
        show={isLoadingPortal}
        message="Opening Billing Portal..."
      />
    </>
  );
}
